import { useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Tag,
  Title,
  Text,
  Icon,
} from '@nimbus-ds/components';
import { ExternalLinkIcon } from '@nimbus-ds/icons';
import { Link } from 'react-router-dom';
import { PATH } from 'App/components/Menu/components/utils';
import { useDesktopMenuContext } from 'App/components/Menu/DesktopMenuProvider';
import { useTopAdBar } from 'App/components/TopAdBar/useTopAdBar';
import { useIsInAppBrowser } from 'App/hooks';
import { ErrorScreen, IonPageStratus, useResponsive } from 'commons/components';
import { PAYMENT_HARDBLOCK, PAYMENT_SOFTBLOCK } from 'config/upsellFlowSources';
import { useIsMobileDevice, useLoggedAdminLink } from 'domains/Auth/hooks';
import { useBillingPlans } from 'domains/Billing/BillingPlans/hooks';
import { CHECKOUT_ROUTES } from 'domains/Billing/Checkout/checkoutRoutes';
import {
  trackingBillingHardBlockPaymentClick,
  trackingBillingSoftBlockPaymentClick,
} from 'domains/Billing/tracking';
import useFindPlanForFeature from 'domains/Billing/UpsellFlow/hooks/useFindPlanForFeature';
import useTranslationBilling from 'domains/Billing/useTranslationBilling';
import { dashboardRoutes } from 'domains/Dashboard';
import { Skeleton } from './Skeleton';
import UpsellBlockImage from './upsell-block-image.png';
import { PlanNameAndPrice } from '../../components/PlanNameAndPrice';
import { UpsellBullets } from '../../components/UpsellBullets';
import { useUpsellBlock } from '../../hooks';
import './UpsellBlockPage.scss';

interface UpsellBlockPageProps {
  isSkippeable?: boolean;
}

function UpsellBlockPage({
  isSkippeable = false,
}: Readonly<UpsellBlockPageProps>) {
  const t = useTranslationBilling('upsellFlow');
  const { showMenu, hideMenu } = useDesktopMenuContext();
  const { hideTopAdBar, showTopAdBar } = useTopAdBar();
  const { goToLoggedAdminLink } = useLoggedAdminLink();
  const { getCurrentPlan } = useFindPlanForFeature();
  const { isLoading, isError, isSuccess, fetchBillingPlans } =
    useBillingPlans();
  const currentPlan = getCurrentPlan();
  const { dismiss } = useUpsellBlock();
  const { isMobile } = useResponsive();
  const isMobileDevice = useIsMobileDevice();
  const isInAppBrowserOpen = useIsInAppBrowser();
  const handleGoToPlansWeb = async () => {
    await goToLoggedAdminLink(PATH.plans);
  };

  useEffect(() => {
    hideMenu();
    hideTopAdBar();
    return () => {
      showMenu();
      showTopAdBar();
    };
  }, [hideMenu, hideTopAdBar, showMenu, showTopAdBar]);

  const handleClickPay = () => {
    if (isSkippeable) {
      trackingBillingSoftBlockPaymentClick({
        featureKey: PAYMENT_SOFTBLOCK,
      });
      dismiss();
    } else {
      trackingBillingHardBlockPaymentClick({
        featureKey: PAYMENT_HARDBLOCK,
      });
    }
  };

  return (
    <IonPageStratus>
      {isError && <ErrorScreen onRetry={fetchBillingPlans} />}
      {!isError && (
        <Box display="flex" justifyContent="center">
          <Box maxWidth="900px">
            <Box
              display="flex"
              flexDirection="column"
              gap="2"
              paddingBottom="6"
            >
              <Title>
                {isSkippeable ? t('softBlock.title') : t('hardBlock.title')}
              </Title>
              <Text>
                {isSkippeable ? t('softBlock.message') : t('hardBlock.message')}
              </Text>
            </Box>
            <div className="stratus--upsell-block-page">
              <Card padding="none">
                <Box display="flex">
                  <Box
                    display="flex"
                    flexDirection="column"
                    flex="1"
                    alignItems="flex-start"
                    padding={isMobile ? '3' : '6'}
                  >
                    {isLoading && <Skeleton />}
                    {isSuccess && currentPlan && (
                      <>
                        <Box display="flex" flexDirection="column" gap="2">
                          <Tag appearance="success">
                            {t('modal.currentPlan')}
                          </Tag>
                          <PlanNameAndPrice plan={currentPlan} align="left" />
                        </Box>
                        <Box paddingTop="4">
                          <UpsellBullets
                            bullets={currentPlan.upsellBullets}
                            progress={currentPlan.upsellBullets.length}
                          />
                        </Box>
                        <Box display="flex" flex="1" />
                        <Box marginTop="4" display="flex" gap="2">
                          {isSkippeable && (
                            <Button
                              as={Link}
                              to={dashboardRoutes.dashboard}
                              onClick={dismiss}
                            >
                              {t('softBlock.cancel')}
                            </Button>
                          )}
                          {isMobileDevice || isInAppBrowserOpen ? (
                            <Button
                              appearance="primary"
                              onClick={handleGoToPlansWeb}
                            >
                              {t('softBlock.seePlans')}
                              <Icon
                                color="currentColor"
                                source={<ExternalLinkIcon />}
                              />
                            </Button>
                          ) : (
                            <Button
                              as={Link}
                              to={`${CHECKOUT_ROUTES.plansAndSubscriptions}?hideAdBars=true`}
                              appearance="primary"
                              onClick={handleClickPay}
                            >
                              {t('softBlock.cta')}
                            </Button>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                  {!isMobile && (
                    <Box
                      display="flex"
                      flexDirection="column"
                      flex="1"
                      alignItems="center"
                      justifyContent="center"
                      padding="6"
                      backgroundColor="neutral-surface"
                    >
                      <img
                        src={UpsellBlockImage}
                        alt="upsell-block-image"
                        width={392}
                        height={268}
                      />
                      <Box maxWidth="280px">
                        <Text
                          textAlign="center"
                          fontSize="caption"
                          color="neutral-textHigh"
                        >
                          {t('softBlock.caption')}
                        </Text>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Card>
            </div>
          </Box>
        </Box>
      )}
    </IonPageStratus>
  );
}

export default UpsellBlockPage;
